import { IWPImage, IWPLink, IWPTitleParts } from "@/src/types";
import React, { useState } from "react";

import { useForm } from "react-hook-form";
import S from "./ContactForm.styled";
import { getUtmTags, sendMessageToQteRest } from "@/src/helpers";
import { gatsbyImageHelper } from "@/src/helpers";
import Checkbox from "@/styles/components/Checkbox";
import axios from "axios";
import ArrowDown from "@/assets/chevron-down.svg";
import { RadioButton } from "@/styles/components";
import {
  getClientIPAddress,
  getHubSpotUserToken,
} from "@/src/helpers/getHsCookieInfo";
import TitleParts from "@/components/global-components/TitleParts";

export interface ResponseData {
  data: {
    inlineMessage: string;
  };
}

export interface IContactFormProps {
  blockId: string;
  formPartiallyHidden: "yes" | "no";
  illustration: IWPImage;
  titleParts: IWPTitleParts;
  title: string;
  subtitle: string;
  checkboxLabels: {
    checkboxLabel1: string;
    checkboxLabel2: string;
    checkboxLabel3: string;
    checkboxLabel4: string;
  };
  privacyPolicyLink: IWPLink;
  unsubscribeText: string;
  submitButtonText: string;
}

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  industry: string;

  emergencyLighting: boolean;
  hvac: boolean;
  lighting: boolean;
  smartLocks: boolean;
  otherSpecific: boolean;

  applications: string;

  moreInformation: boolean;
  buildingSolution: boolean;
  sourcingSolution: boolean;
  other: boolean;
  message: string;

  newsletter: boolean;

  legalConsent: boolean;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyFrom = (): FormData => ({
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  industry: "",

  emergencyLighting: false,
  hvac: false,
  lighting: false,
  smartLocks: false,
  otherSpecific: false,

  applications: "",

  moreInformation: false,
  buildingSolution: false,
  sourcingSolution: false,
  other: false,
  message: "",

  newsletter: false,

  legalConsent: false,
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyFrom(),
  success: false,
});

const ContactForm: React.FC<IContactFormProps> = ({
  blockId = "",
  illustration,
  titleParts,
  title,
  subtitle,
  checkboxLabels,
  privacyPolicyLink,
  unsubscribeText,
  submitButtonText,
  formPartiallyHidden,
}) => {
  const [formState, setFormState] = useState(emptyFormState());
  //useState to store response message, to be displayed after form submission as dangerouslySetInnerHTML
  const [responseMessage, setResponseMessage] = useState("");
  const [hideForm, setHideForm] = useState(formPartiallyHidden === "yes");

  const utmTags = getUtmTags();

  function updateFormProperty<K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ) {
    //if checkbox, toggle value
    if (typeof value === "boolean") {
      setFormState((state) => ({
        ...state,
        data: {
          ...state.data,
          [property]: !state.data[property],
        },
      }));
      return;
    }

    setFormState((state) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({});

  const industries = [
    { label: "Industry *", value: "", disabled: true, selected: true },
    {
      label: "Manufacturing: reduce downtime through condition monitoring",
      value: "Smart Manufacturing",
    },
    {
      label: "Buildings: connect sensors or lights in buildings",
      value: "Smart Building",
    },
    { label: "Energy: smart electricity metering", value: "Smart Energy" },
    {
      label: "Tracking: keep track of assets and people",
      value: "Smart Tracking",
    },
    { label: "Other (specify in Message)", value: "Other" },
  ];

  const url =
    "https://api.hsforms.com/submissions/v3/integration/submit/4245941/efd8e5c3-3d31-4628-bfa9-3bc0b3f5dde8";

  const onSubmit = handleSubmit(async () => {
    if (typeof window === "undefined") return;

    setFormState((state) => ({ ...state, isLoading: true }));
    const userToken = getHubSpotUserToken();
    const ipAddress = await getClientIPAddress();

    axios({
      method: "post",
      url,
      data: {
        fields: [
          /* Contact Info */
          { name: "email", value: formState.data.email },
          { name: "firstname", value: formState.data.firstName },
          { name: "lastname", value: formState.data.lastName },
          { name: "company", value: formState.data.company },

          /* Industry */
          { name: "vertical", value: formState.data.industry },

          /* Applications */
          {
            name: "0-2/applications",
            value: formState.data.applications || "",
          },

          /* Interest */
          {
            name: "interest",
            value: formState.data.moreInformation
              ? "More information about Wirepas"
              : "",
          },
          {
            name: "interest",
            value: formState.data.buildingSolution
              ? "Building a solution with Wirepas Technology"
              : "",
          },
          {
            name: "interest",
            value: formState.data.sourcingSolution
              ? "Sourcing solutions or hardware from the Wirepas Ecosystem"
              : "",
          },
          {
            name: "interest",
            value: formState.data.other ? "Other" : "",
          },

          /* Message */
          { name: "message", value: formState.data.message },

          /* utm */
          { name: "utm_source", value: utmTags?.utm_source || "" },
          { name: "utm_medium", value: utmTags?.utm_medium || "" },
          { name: "utm_campaign", value: utmTags?.utm_campaign || "" },
          { name: "utm_content", value: utmTags?.utm_content || "" },

          /* newsletter */
          {
            name: "subscribe_to_our_newsletter",
            value: formState.data.newsletter ? "true" : "",
          },
        ],
        legalConsentOptions: {
          consent: {
            consentToProcess: formState.data.legalConsent,
            text: "Text that gives consent to process",
          },
        },
        context: {
          hutk: userToken,
          ipAddress: ipAddress,
          pageUri: window.location.href,
          pageName: document.title,
        },
      },
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res: ResponseData) => {
        if (res.data.inlineMessage) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
          setResponseMessage(res.data.inlineMessage);

          //scroll to get-in-touch-section
          const responseMessageElement = document.getElementById(
            "get-in-touch-section"
          );
          const offset = -100;
          responseMessageElement &&
            window.scrollTo({
              top: responseMessageElement.offsetTop + offset,
              behavior: "smooth",
            });
        }
      })
      .catch((error) => {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          error: "Something went wrong, please try again later",
        }));
        console.error("Post error: ", error);
        sendMessageToQteRest(error.message, "Contact form");
      });
  });

  const imageData = gatsbyImageHelper(illustration);

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    keyName: keyof FormData
  ) => {
    updateFormProperty(keyName, event.target.checked);
  };

  //if industry is set to Buildings, show radio buttons, and set "Other" as default
  React.useEffect(() => {
    if (formState.data.industry === "Smart Building") {
      updateFormProperty("applications", "Other");
    }
  }, [formState.data.industry]);

  return (
    <S.Section id="get-in-touch-section">
      <S.Inner id={blockId}>
        <div>
          <S.ImageTitleWrapper>
            {imageData && (
              <S.Image
                image={imageData}
                alt={illustration.altText}
                objectFit="cover"
              />
            )}
            <S.TitleWrapper>
              <S.Title>
                {titleParts ? (
                  <TitleParts titleParts={titleParts} backgroundColor="black" />
                ) : (
                  title && title
                )}
              </S.Title>
              {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
            </S.TitleWrapper>
          </S.ImageTitleWrapper>

          {/* form starts here */}
          {formState.success ? (
            <S.ResponseMessage
              dangerouslySetInnerHTML={{ __html: responseMessage }}
            />
          ) : (
            <S.Form onSubmit={onSubmit} $hideForm={hideForm}>
              <S.HiddenFormOverlay
                onClick={() => setHideForm(false)}
                $hideForm={hideForm}
                $backgroundColor={"black"}
              />

              {/* First Name */}
              <S.InputWrapper>
                <S.Input
                  onFocus={() => setHideForm(false)}
                  type="text"
                  placeholder="First name *"
                  {...register("firstName", { required: true })}
                  onChange={(e) => {
                    updateFormProperty("firstName", e.target.value);
                    setValue("firstName", e.target.value);
                  }}
                />
                {errors.firstName && (
                  <S.ErrorMsg>First name is required</S.ErrorMsg>
                )}
              </S.InputWrapper>

              {/* Last Name */}
              <S.InputWrapper>
                <S.Input
                  onFocus={() => setHideForm(false)}
                  type="text"
                  placeholder="Last name *"
                  {...register("lastName", { required: true })}
                  onChange={(e) => {
                    updateFormProperty("lastName", e.target.value);
                    setValue("lastName", e.target.value);
                  }}
                />
                {errors.lastName && (
                  <S.ErrorMsg>Last name is required</S.ErrorMsg>
                )}
              </S.InputWrapper>

              {/* Email */}
              <S.InputWrapper>
                <S.Input
                  onFocus={() => setHideForm(false)}
                  type="email"
                  placeholder="Email *"
                  {...register("email", { required: true })}
                  onChange={(e) => {
                    updateFormProperty("email", e.target.value);
                    setValue("email", e.target.value);
                  }}
                />
                {errors.email && <S.ErrorMsg>Email is required</S.ErrorMsg>}
              </S.InputWrapper>

              {/* Company */}
              <S.InputWrapper>
                <S.Input
                  type="text"
                  placeholder="Company *"
                  {...register("company", { required: true })}
                  onChange={(e) => {
                    updateFormProperty("company", e.target.value);
                    setValue("company", e.target.value);
                  }}
                />
                {errors.company && <S.ErrorMsg>Company is required</S.ErrorMsg>}
              </S.InputWrapper>

              {/* Industry */}
              <S.InputWrapper>
                <S.Select
                  {...register("industry", { required: true })}
                  onChange={(e) =>
                    updateFormProperty("industry", e.target.value)
                  }
                >
                  {industries.map((industry) => (
                    <option
                      key={industry.value}
                      value={industry.value}
                      disabled={industry.disabled}
                      selected={industry.selected}
                    >
                      {industry.label}
                    </option>
                  ))}
                </S.Select>
                <ArrowDown />
                {errors.industry && (
                  <S.ErrorMsg>Industry is required</S.ErrorMsg>
                )}
              </S.InputWrapper>

              {/* If buildings, show radio buttons */}
              {formState.data.industry === "Smart Building" && (
                <>
                  <S.Text>Applications *</S.Text>

                  {/* Emergency Lighting */}
                  <div
                    onClick={() => {
                      updateFormProperty("applications", "Emergency lighting");
                      setValue("applications", "Emergency lighting");
                    }}
                  >
                    <RadioButton
                      id="emergency-lighting"
                      checked={
                        formState.data.applications === "Emergency lighting"
                      }
                      label="Emergency lighting"
                    />
                  </div>

                  {/* HVAC */}
                  <div
                    onClick={() => {
                      updateFormProperty("applications", "HVAC");
                      setValue("applications", "HVAC");
                    }}
                  >
                    <RadioButton
                      id="hvac"
                      checked={formState.data.applications === "HVAC"}
                      label="HVAC"
                    />
                  </div>

                  {/* Lighting */}
                  <div
                    onClick={() => {
                      updateFormProperty("applications", "Lighting");
                      setValue("applications", "Lighting");
                    }}
                  >
                    <RadioButton
                      id="lighting"
                      checked={formState.data.applications === "Lighting"}
                      label="Lighting"
                    />
                  </div>

                  {/* Smart Locks */}
                  <div
                    onClick={() => {
                      updateFormProperty("applications", "Smart locks");
                      setValue("applications", "Smart locks");
                    }}
                  >
                    <RadioButton
                      id="smart-locks"
                      checked={formState.data.applications === "Smart locks"}
                      label="Smart locks"
                    />
                  </div>

                  {/* Other Specific */}
                  <div
                    onClick={() => {
                      updateFormProperty("applications", "Other");
                      setValue("applications", "Other");
                    }}
                  >
                    <RadioButton
                      id="other-specific"
                      checked={formState.data.applications === "Other"}
                      label="Other (specify in Message)"
                    />
                  </div>
                </>
              )}

              {/* Checkboxes */}
              <S.Text>Interested in</S.Text>
              <Checkbox
                checked={formState.data.moreInformation}
                {...register("moreInformation", { required: false })}
                onChange={(e) => {
                  handleCheckboxChange(e, "moreInformation");
                  setValue("moreInformation", e.target.checked);
                }}
                label={checkboxLabels.checkboxLabel1}
              />

              <Checkbox
                checked={formState.data.buildingSolution}
                {...register("buildingSolution", { required: false })}
                onChange={(e) => {
                  handleCheckboxChange(e, "buildingSolution");
                  setValue("buildingSolution", e.target.checked);
                }}
                label={checkboxLabels.checkboxLabel2}
              />

              <Checkbox
                checked={formState.data.sourcingSolution}
                {...register("sourcingSolution", { required: false })}
                onChange={(e) => {
                  handleCheckboxChange(e, "sourcingSolution");
                  setValue("sourcingSolution", e.target.checked);
                }}
                label={checkboxLabels.checkboxLabel3}
              />

              <Checkbox
                checked={formState.data.other}
                {...register("other", { required: false })}
                onChange={(e) => {
                  handleCheckboxChange(e, "other");
                  setValue("other", e.target.checked);
                }}
                label={checkboxLabels.checkboxLabel4}
              />

              {/* Message */}
              <S.Textarea
                placeholder="Message"
                {...register("message", { required: false })}
                onChange={(e) => {
                  updateFormProperty("message", e.target.value);
                  setValue("message", e.target.value);
                }}
              />

              {/* Newsletter */}
              <S.InputWrapper>
                <Checkbox
                  checked={formState.data.newsletter}
                  {...register("newsletter", { required: false })}
                  onChange={(e) => {
                    handleCheckboxChange(e, "newsletter");
                    setValue("newsletter", e.target.checked);
                  }}
                  label="Subscribe to our newsletter for some kick-ass IoT news"
                />
              </S.InputWrapper>

              {/* privacy policy */}
              <S.InputWrapper>
                <Checkbox
                  checked={formState.data.legalConsent}
                  {...register("legalConsent", { required: true })}
                  onChange={(e) => {
                    handleCheckboxChange(e, "legalConsent");
                    setValue("legalConsent", e.target.checked);
                  }}
                  link={privacyPolicyLink}
                />
                {errors.legalConsent && (
                  <S.ErrorMsg>Legal consent is required</S.ErrorMsg>
                )}
              </S.InputWrapper>

              <S.Text>{unsubscribeText}</S.Text>
              <S.Text>{submitButtonText}</S.Text>
              <S.Submit disabled={formState.isLoading} type="submit">
                SEND
              </S.Submit>
              {/* form error */}
              {formState.error && <S.ErrorMsg>{formState.error}</S.ErrorMsg>}
            </S.Form>
          )}
        </div>
      </S.Inner>
    </S.Section>
  );
};

export default ContactForm;
